import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Table,
  Input,
  Space,
  Tag,
  Select,
  Button,
  Upload,
  notification,
  Popconfirm,
  message,
  Tabs,
  Progress,
  Tooltip,
  Descriptions,
} from "antd";
import {
  PlusOutlined,
  UploadOutlined,
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  EyeOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import moment from "moment";
import { getReq, postReq } from "../api";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import AddOutreachModal from "../components/addOutreachModel";
import CreateSpocModal from "../components/addSpocModel";

// New campaign components
import CreateTemplateModal from "../components/CreateTemplateModal";
import CreateCampaignModal from "../components/CreateCampaignModal";
import CampaignDetailsModal from "../components/CampaignDetailsModal";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

// 🔵🟡🟠🔴🟢 Stage colors
const stageColorMap = {
  INTRO: "blue",
  FU1: "gold",
  FU2: "orange",
  FU3: "red",
  REPLIED: "green",
};

const statusColorMap = {
  PENDING: "orange",
  IN_DISCUSSION: "green",
  REPLIED: "blue",
  NOT_INTERESTED: "red",
  NO_REPLY: "gold",
  COMPLETED: "green",
  RUNNING: "orange",
  SCHEDULED: "blue",
  PAUSED: "default",
  FAILED: "red",
};

const summaryStages = [
  { key: "TOTAL", label: "Total" },
  { key: "INTRO", label: "Intro" },
  { key: "FU1", label: "FU1" },
  { key: "FU2", label: "FU2" },
  { key: "FU3", label: "FU3" },
  { key: "REPLIED", label: "Replied" },
];

export default function OutreachTablePage() {
  const history = useHistory();

  // Active Tab State
  const [activeTab, setActiveTab] = useState("opportunities");

  // --- Tab 1: Opportunities (Pipeline) States ---
  const [tableData, setTableData] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState(null);
  const [selectedStage, setSelectedStage] = useState("TOTAL");
  const [search, setSearch] = useState("");
  const [tableParams, setTableParams] = useState({
    page: 1,
    limit: 20,
  });
  const [total, setTotal] = useState(0);

  // --- Tab 2: SPOC Directory States ---
  const [spocs, setSpocs] = useState([]);
  const [loadingSpocs, setLoadingSpocs] = useState(false);
  const [spocSearch, setSpocSearch] = useState("");
  const [spocParams, setSpocParams] = useState({
    page: 1,
    limit: 20,
  });
  const [spocTotal, setSpocTotal] = useState(0);
  const [selectedSpocIds, setSelectedSpocIds] = useState([]);
  const [spocStatsData, setSpocStatsData] = useState({
    totalCompanies: 0,
    companiesWithSpoc: 0,
    totalSpocs: 0,
  });
  const [openSpocModal, setOpenSpocModal] = useState(false);

  // --- Tab 3: Templates States ---
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [openTemplateModal, setOpenTemplateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // --- Tab 4: Campaigns States ---
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [openCampaignModal, setOpenCampaignModal] = useState(false);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);

  // ===========================================================================
  // 📡 API CALLS
  // ===========================================================================

  // --- Tab 1: Opportunities ---
  const fetchSummary = async () => {
    const res = await postReq("common/outreach/summary/all");
    if (res.status == 1) {
      setSummary(res.data);
    } else {
      message.error(res.message);
    }
  };

  const fetchTableData = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (selectedStage !== "TOTAL") params.append("stage", selectedStage);
    if (search) params.append("search", search);

    const res = await postReq(`common/outreach/table?${params.toString()}`, {
      ...tableParams,
    });

    setTableData(res.data || []);
    setTotal(res.count || 0);
    setLoading(false);
  };

  const handleUploadExcel = async (file) => {
    try {
      const formData = new FormData();
      formData.append("excel", file);
      const res = await postReq("common/upload_outreach_bulk", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        responseType: "blob",
      });

      const blob = new Blob([res], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "outreach_import_result.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();

      notification.success({
        message: "Import Completed",
        description: "Excel downloaded with status & remarks",
        placement: "topRight",
      });
      fetchTableData();
    } catch (error) {
      notification.error({
        message: "Upload Failed",
        description: "Something went wrong",
        placement: "topRight",
      });
    }
  };

  // --- Tab 2: SPOC Directory ---
  const fetchSpocs = async () => {
    setLoadingSpocs(true);
    try {
      const res = await postReq("common/spoc", {
        page: spocParams.page,
        limit: spocParams.limit,
        search: spocSearch,
      });
      if (res.status === 1) {
        setSpocs(res.data || []);
        setSpocTotal(res.meta?.total || 0);
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to load SPOCs");
    } finally {
      setLoadingSpocs(false);
    }
  };

  const fetchSpocStats = async () => {
    try {
      const res = await postReq("common/spocStats", {});
      if (res.status === 1) {
        setSpocStatsData(res.data);
      }
    } catch (error) { }
  };

  const handleUploadSpocExcel = async (file) => {
    try {
      const formData = new FormData();
      formData.append("excel", file);
      const res = await postReq("common/upload_spoc_bulk", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        responseType: "blob",
      });

      const blob = new Blob([res], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "spoc_import_result.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();

      notification.success({
        message: "Import Completed",
        description: "SPOCs imported. Excel downloaded with status & remarks",
        placement: "topRight",
      });
      fetchSpocs();
      fetchSpocStats();
    } catch (error) {
      notification.error({
        message: "Upload Failed",
        description: "Something went wrong",
        placement: "topRight",
      });
    }
  };

  // --- Tab 3: Templates ---
  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await getReq("common/templates", {});
      if (res.status === 1) {
        setTemplates(res.data || []);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    try {
      const res = await postReq(`/common/templates/delete/${id}`, {});
      if (res.status === 1) {
        message.success("Template deleted successfully");
        fetchTemplates();
      } else {
        message.error(res.message || "Failed to delete template");
      }
    } catch (error) {
      console.error(error);
      message.error("Error deleting template");
    }
  };

  // --- Tab 4: Campaigns ---
  const fetchCampaigns = async () => {
    setLoadingCampaigns(true);
    try {
      const res = await getReq("common/campaigns", {});
      if (res.status === 1) {
        setCampaigns(res.data || []);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const handleToggleCampaign = async (id, currentStatus) => {
    const newStatus = currentStatus === "PAUSED" ? "RUNNING" : "PAUSED";
    try {
      const res = await postReq(`/common/campaigns/${id}/toggle`, { status: newStatus });
      if (res.status === 1) {
        message.success(`Campaign ${newStatus === "RUNNING" ? "resumed" : "paused"} successfully`);
        fetchCampaigns();
      } else {
        message.error(res.message || "Failed to update campaign status");
      }
    } catch (error) {
      console.error(error);
      message.error("Error updating campaign status");
    }
  };

  // ===========================================================================
  // 🔄 EFFECTS
  // ===========================================================================

  useEffect(() => {
    if (activeTab === "opportunities") {
      fetchSummary();
      fetchTableData();
    } else if (activeTab === "spocs") {
      fetchSpocStats();
      fetchSpocs();
    } else if (activeTab === "templates") {
      fetchTemplates();
    } else if (activeTab === "campaigns") {
      fetchCampaigns();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "opportunities") {
      fetchSummary();
      fetchTableData();
    }
  }, [selectedStage, search, tableParams]);

  useEffect(() => {
    if (activeTab === "spocs") {
      fetchSpocs();
    }
  }, [spocSearch, spocParams]);

  // ===========================================================================
  // 📋 TABLE COLUMNS
  // ===========================================================================

  // --- Opportunities Columns ---
  const opportunitiesColumns = [
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Button
          type="link"
          onClick={(e) => {
            e.stopPropagation();
            history.push(`/company/spoc`, { record });
          }}
        >
          View Timeline
        </Button>
      ),
    },
    {
      title: "Company / SPOC",
      dataIndex: "companyName",
      render: (_, record) => (
        <>
          <strong>{record.companyName}</strong>
          <div style={{ fontSize: 12, color: "#666" }}>{record.spocName}</div>
        </>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
    },
    {
      title: "Stage",
      dataIndex: "currentStage",
      render: (stage) => <Tag color={stageColorMap[stage]}>{stage}</Tag>,
    },
    {
      title: "Last Contacted",
      dataIndex: "lastActionDate",
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY") : "-"),
    },
    {
      title: "Follow-up",
      dataIndex: "nextFollowUpDate",
      render: (date) => {
        if (!date) return "-";
        const diff = dayjs(date).diff(dayjs(), "day");
        if (diff < 0) return <Tag color="red">Overdue</Tag>;
        if (diff === 0) return <Tag color="gold">Today</Tag>;
        return <Tag color="blue">{diff}d to go</Tag>;
      },
    },
    {
      title: "Touches",
      dataIndex: "totalTouches",
      align: "center",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => <Tag color={statusColorMap[status]}>{status}</Tag>,
    },
    {
      title: "Owner",
      dataIndex: "ownerName",
    },
  ];

  // --- SPOC Directory Columns ---
  const spocsColumns = [
    {
      title: "Name",
      key: "name",
      render: (_, record) => (
        <>
          <strong>{record.firstName} {record.lastName}</strong>
          <div style={{ fontSize: 12, color: "#666" }}>
            {record.region || "India"} {record.spocFrom ? `• ${record.spocFrom}` : ""}
          </div>
        </>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      render: (text) => (
        <a
          href={`https://outlook.office.com/mail/deeplink/compose?to=${text}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#3699FF", textDecoration: "underline" }}
        >
          {text}
        </a>
      ),
    },
    {
      title: "Mobile",
      dataIndex: "phone",
      render: (text) => text || "-",
    },
    {
      title: "Designation",
      dataIndex: "designation",
      render: (text) => text || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => <Tag color={statusColorMap[status]}>{status}</Tag>,
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      render: (text) => dayjs(text).format("DD/MM/YYYY HH:mm"),
    },
  ];

  // --- Campaigns Columns ---
  const campaignsColumns = [
    {
      title: "Campaign Name",
      dataIndex: "name",
      key: "name",
      render: (text, r) => <strong>{text}</strong>,
    },
    {
      title: "Template",
      dataIndex: "templateId",
      key: "template",
      render: (template) => template?.name || "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <Tag color={statusColorMap[status]}>{status}</Tag>,
    },
    {
      title: "Progress",
      key: "progress",
      width: 250,
      render: (_, r) => {
        const total = r.totalRecipients || 0;
        const processed = (r.sentCount || 0) + (r.failedCount || 0);
        const percent = total > 0 ? Math.round((processed / total) * 100) : 0;
        return (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
              <span>{processed} / {total} Sent</span>
              <span>{percent}%</span>
            </div>
            <Progress percent={percent} size="small" showInfo={false} status={r.status === "FAILED" ? "exception" : "active"} />
          </div>
        );
      },
    },
    {
      title: "Created On",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => dayjs(date).format("DD MMM YYYY HH:mm"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, r) => (
        <Space size="middle">
          <Tooltip title="View Logs & Stats">
            <Button
              type="primary"
              shape="circle"
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedCampaignId(r._id);
                setOpenDetailsModal(true);
              }}
            />
          </Tooltip>
          {["RUNNING", "SCHEDULED", "PAUSED"].includes(r.status) && (
            <Tooltip title={r.status === "PAUSED" ? "Resume Campaign" : "Pause Campaign"}>
              <Button
                type="default"
                shape="circle"
                icon={r.status === "PAUSED" ? <PlayCircleOutlined /> : <PauseCircleOutlined />}
                onClick={() => handleToggleCampaign(r._id, r.status)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 4 }}>
      <Title level={3} style={{ color: "#13487D", marginBottom: 16 }}>Outreach Dashboard</Title>

      <Tabs activeKey={activeTab} onChange={(key) => setActiveTab(key)} type="card">
        {/* =================================================================== */}
        {/* TAB 1: OPPORTUNITIES (PIPELINE) */}
        {/* =================================================================== */}
        <TabPane tab="Opportunities Pipeline" key="opportunities">
          {/* Summary Bar */}
          <Row gutter={16} style={{ marginBottom: 24 }}>
            {summaryStages.map((item) => (
              <Col key={item.key}>
                <Card
                  hoverable
                  onClick={() => setSelectedStage(item.key)}
                  style={{
                    width: 140,
                    border:
                      selectedStage === item.key
                        ? "2px solid #1677ff"
                        : "1px solid #f0f0f0",
                  }}
                >
                  <Text type="secondary">{item.label}</Text>
                  <Title level={3} style={{ margin: 0 }}>
                    {summary[item.key] || 0}
                  </Title>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Filters Row */}
          <Space
            style={{
              width: "100%",
              justifyContent: "space-between",
              marginBottom: 16,
            }}
          >
            <Input.Search
              placeholder="Search by name or email"
              allowClear
              style={{ width: 300 }}
              onSearch={(val) => setSearch(val)}
            />

            <Space>
              <Select
                defaultValue="last_contacted"
                style={{ width: 180 }}
                options={[{ value: "last_contacted", label: "Last contacted" }]}
              />
              <Button onClick={fetchTableData}>Refresh</Button>

              <Upload
                accept=".csv,.xls,.xlsx"
                fileList={fileList}
                maxCount={1}
                beforeUpload={() => false}
                onChange={(info) => {
                  const newFileList = info.fileList.slice(-1);
                  setFileList(newFileList);
                  const file = newFileList[0]?.originFileObj;
                  if (file) {
                    handleUploadExcel(file);
                  }
                }}
              >
                <Button icon={<UploadOutlined />}>Upload Outreach CSV</Button>
              </Upload>
            </Space>
          </Space>

          {/* Main Table */}
          <Table
            loading={loading}
            columns={opportunitiesColumns}
            dataSource={tableData}
            rowKey="spocId"
            pagination={{
              current: tableParams.page,
              pageSize: tableParams.limit,
              total,
              showSizeChanger: true,
              pageSizeOptions: [5, 10, 20, 30],
              showTotal: (total, range) => (
                <span style={{ color: "#6b7280" }}>
                  Showing {range[0]}–{range[1]} of {total}
                </span>
              ),
            }}
            onChange={(pagination) => {
              setTableParams({
                page: pagination.current,
                limit: pagination.pageSize,
              });
            }}
            scroll={{ x: "max-content" }}
          />
        </TabPane>

        {/* =================================================================== */}
        {/* TAB 2: SPOC DIRECTORY */}
        {/* =================================================================== */}
        <TabPane tab="SPOC Directory" key="spocs">
          {/* SPOC Stats */}
          <div style={{ display: "flex", gap: 20, marginBottom: "20px" }}>
            <Card style={{ minWidth: 180 }}>
              <Text type="secondary">Total SPOCs</Text>
              <Title level={4} style={{ margin: 0 }}>{spocStatsData.totalSpocs || 0}</Title>
            </Card>
            <Card style={{ minWidth: 180 }}>
              <Text type="secondary">Accounts with SPOC</Text>
              <Title level={4} style={{ margin: 0 }}>{spocStatsData.companiesWithSpoc || 0}</Title>
            </Card>
            <Card style={{ minWidth: 180 }}>
              <Text type="secondary">Total Accounts</Text>
              <Title level={4} style={{ margin: 0 }}>{spocStatsData.totalCompanies || 0}</Title>
            </Card>
          </div>

          {/* Actions Bar */}
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
            <Space>
              <Input.Search
                placeholder="Search SPOCs..."
                allowClear
                style={{ width: 300 }}
                onSearch={(val) => setSpocSearch(val)}
              />
              {selectedSpocIds.length > 0 && (
                <Button
                  type="primary"
                  onClick={() => setOpenCampaignModal(true)}
                  style={{ background: "#52c41a", borderColor: "#52c41a" }}
                >
                  Launch Campaign ({selectedSpocIds.length} selected)
                </Button>
              )}
            </Space>

            <Space>
              <Upload
                accept=".csv,.xls,.xlsx"
                maxCount={1}
                beforeUpload={() => false}
                onChange={(info) => {
                  const file = info.fileList.slice(-1)[0]?.originFileObj;
                  if (file) handleUploadSpocExcel(file);
                }}
                showUploadList={false}
              >
                <Button icon={<UploadOutlined />}>Upload SPOC Excel</Button>
              </Upload>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setOpenSpocModal(true)}
              >
                Add SPOC
              </Button>
            </Space>
          </div>

          {/* SPOC Table */}
          <Table
            loading={loadingSpocs}
            columns={spocsColumns}
            dataSource={spocs}
            rowKey="_id"
            rowSelection={{
              selectedRowKeys: selectedSpocIds,
              onChange: (keys) => setSelectedSpocIds(keys),
            }}
            pagination={{
              current: spocParams.page,
              pageSize: spocParams.limit,
              total: spocTotal,
              showSizeChanger: true,
              pageSizeOptions: [5, 10, 20, 30],
              showTotal: (total, range) => (
                <span style={{ color: "#6b7280" }}>
                  Showing {range[0]}–{range[1]} of {total}
                </span>
              ),
            }}
            onChange={(pagination) => {
              setSpocParams({
                page: pagination.current,
                limit: pagination.pageSize,
              });
            }}
            scroll={{ x: "max-content" }}
          />
        </TabPane>

        {/* =================================================================== */}
        {/* TAB 3: EMAIL TEMPLATES */}
        {/* =================================================================== */}
        <TabPane tab="Email Templates" key="templates">
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setSelectedTemplate(null);
                setOpenTemplateModal(true);
              }}
            >
              Create Template
            </Button>
          </div>

          <Row gutter={[16, 16]}>
            {templates.map((t) => (
              <Col span={8} key={t._id}>
                <Card
                  title={t.name}
                  extra={t.isSample ? <Tag color="blue">Sample</Tag> : null}
                  actions={
                    t.isSample
                      ? []
                      : [
                        <Tooltip title="Edit">
                          <EditOutlined
                            key="edit"
                            onClick={() => {
                              setSelectedTemplate(t);
                              setOpenTemplateModal(true);
                            }}
                          />
                        </Tooltip>,
                        <Popconfirm
                          title="Delete this template?"
                          onConfirm={() => handleDeleteTemplate(t._id)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Tooltip title="Delete">
                            <DeleteOutlined key="delete" style={{ color: "#ff4d4f" }} />
                          </Tooltip>
                        </Popconfirm>,
                      ]
                  }
                >
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Subject">
                      <span style={{ fontWeight: 500 }}>{t.subject}</span>
                    </Descriptions.Item>
                  </Descriptions>
                  <div
                    style={{
                      marginTop: 12,
                      maxHeight: 80,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      color: "#666",
                      fontSize: 12,
                      whiteSpace: "pre-line",
                    }}
                  >
                    {t.body.replace(/<[^>]*>/g, "")}
                  </div>
                </Card>
              </Col>
            ))}
            {templates.length === 0 && !loadingTemplates && (
              <Col span={24} style={{ textAlign: "center", padding: 40, color: "#999" }}>
                No templates found. Click "Create Template" to get started!
              </Col>
            )}
          </Row>
        </TabPane>

        {/* =================================================================== */}
        {/* TAB 4: CAMPAIGNS */}
        {/* =================================================================== */}
        <TabPane tab="Email Campaigns" key="campaigns">
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setOpenCampaignModal(true)}
            >
              New Campaign
            </Button>
          </div>

          <Table
            loading={loadingCampaigns}
            columns={campaignsColumns}
            dataSource={campaigns}
            rowKey="_id"
            pagination={{ pageSize: 10 }}
          />
        </TabPane>
      </Tabs>

      {/* =================================================================== */}
      {/* MODALS */}
      {/* =================================================================== */}
      <CreateTemplateModal
        open={openTemplateModal}
        template={selectedTemplate}
        onClose={() => {
          setOpenTemplateModal(false);
          setSelectedTemplate(null);
        }}
        onSuccess={fetchTemplates}
      />

      <CreateCampaignModal
        open={openCampaignModal}
        selectedSpocIds={selectedSpocIds}
        onClose={() => {
          setOpenCampaignModal(false);
          setSelectedSpocIds([]);
        }}
        onSuccess={() => {
          setActiveTab("campaigns");
          fetchCampaigns();
        }}
      />

      <CampaignDetailsModal
        open={openDetailsModal}
        campaignId={selectedCampaignId}
        onClose={() => {
          setOpenDetailsModal(false);
          setSelectedCampaignId(null);
        }}
      />

      <CreateSpocModal
        open={openSpocModal}
        onClose={() => setOpenSpocModal(false)}
        onSuccess={() => {
          fetchSpocs();
          fetchSpocStats();
        }}
      />
    </div>
  );
}
